import { FormItem, Input, Label, Text } from '@ui5/webcomponents-react';
import { SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import {
  IssueType,
  MarketingQueryDetailsFragment,
  useUpdateMarketingQueryMutation
} from 'generated/graphql';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBaseQueryStore } from 'stores/UpdateBaseQueryStore';
import { useUpdateMarketingQueryStore } from 'stores/UpdateMarketingQueryStore';
import { useEditQueryStore } from 'stores/EditQueryStore';
import { FormDisplayItem } from '..';
import { useStyles } from '../DisplayQuery.jss';
import dayjs from 'dayjs';

export interface DisplayQueryDetailsMarketingProps {
  queryId: string;
  queryDetails: MarketingQueryDetailsFragment;
}

export const DisplayQueryDetailsMarketing = forwardRef<
  SpecificQueryType,
  DisplayQueryDetailsMarketingProps
>((props: DisplayQueryDetailsMarketingProps, ref) => {
  const { queryDetails, queryId } = props;
  const { t } = useTranslation();
  const marketingQueryStore = useUpdateMarketingQueryStore();
  const baseQueryStore = useUpdateBaseQueryStore();
  const { editMode, setCancelEditing } = useEditQueryStore();
  const [, callUpdateQuery] = useUpdateMarketingQueryMutation();
  const classes = useStyles();

  useImperativeHandle(ref, () => ({
    cancel: () => {
      marketingQueryStore.setInitialStateWithProps(queryDetails);
    },
    save: () => {
      return saveQuery();
    },
    isValid: () => {
      return true;
    }
  }));

  useEffect(() => {
    marketingQueryStore.setInitialStateWithProps(queryDetails);
  }, []);

  const onChangeFileName = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    marketingQueryStore.setFileName(value);
  };

  const saveQuery = async () => {
    const result = await callUpdateQuery({
      input: {
        id: Number(queryId),
        sourceText: baseQueryStore.sourceText,
        targetText: baseQueryStore.targetText,
        deadline: dayjs(baseQueryStore.deadline).format('YYYY-MM-DD'),
        targetLanguage: baseQueryStore.targetLanguage,
        issueType: baseQueryStore.selectedIssueType as unknown as IssueType,
        details: baseQueryStore.details,
        ...(baseQueryStore.assignee && { assignee: baseQueryStore.assignee }),
        distributions: baseQueryStore.distributions,
        resetedDistributions: baseQueryStore.resetedDistributions,
        fileName: marketingQueryStore.fileName,
        qeReview: baseQueryStore.qeReview
      }
    });
    return { error: result.error };
  };

  const getDetailsData = (): FormDisplayItem[] => {
    return [
      {
        renderComponent: () => (
          <Input
            value={marketingQueryStore.fileName}
            aria-labelledby={t('display-query-details-marketing-file-name')}
            data-testid="edit-file-name"
            data-help-id="edit-file-name"
            className={classes.formLineInput}
            onChange={onChangeFileName}
          />
        ),
        dataTestId: 'file-name',
        data: marketingQueryStore.fileName,
        i18nTag: t('display-query-details-marketing-file-name')
      }
    ];
  };

  let queryDetailsData: FormDisplayItem[] = getDetailsData();

  useEffect(() => {
    queryDetailsData = getDetailsData();
  }, [queryDetails]);

  return (
    <>
      {queryDetailsData.map((entry) => (
        <FormItem
          key={entry.dataTestId}
          labelContent={
            <Label
              id={entry.i18nTag}
              required={editMode && entry.required}
              data-help-id={entry.dataTestId}
              className={classes.formItemLabel}
              wrappingType="None"
            >
              {entry.i18nTag}
            </Label>
          }
        >
          {editMode && entry.editable !== false ? (
            entry.renderComponent()
          ) : (
            <Text
              data-testid={entry.dataTestId}
              aria-labelledby={entry.i18nTag}
              className={classes.formItemText}
            >
              {entry.data}
            </Text>
          )}
        </FormItem>
      ))}
    </>
  );
});
